import _orderBy from 'lodash/orderBy'
import _sortBy from 'lodash/sortBy'
import latinize from 'latinize'
import { PROMISE_STATUS } from 'frontcore/constants/promises'

import {
  getAuditConfigByRoles,
  getAuditConfigByTeams,
  getAuditSubSources,
  getAuditTemplateSubsources,
  getLPAListTemplates,
  getLPAsCriteriaInTemplate,
  getMasterAuditPercentageRules,
  getMasterAuditPercentageScores,
  patchCriteriaInTemplate,
  patchTemplate,
  patchTemplateLevelItems,
  removeCriterionInTemplate,
  updateAuditTemplateSubtypes,
} from 'frontend/communications/templates'
import { getMasterAuditSources } from 'frontend/communications/apiClient'

const stateObj = {
  auditConfigByRoles: [],
  criteria: [],
  criteriaInTemplate: [],
  statusAuditSubsources: [],
  sources: [],
  subSources: [],
  templates: [],
  templateFiltersPromise: null,
  templateFiltersLoadingStatus: PROMISE_STATUS.notStarted,
  percentageRules: [],
  percentageScores: [],
}

const moduleGetters = {
  getCriteria (state) {
    return state.criteria
  },
  getCriteriaInTemplate (state) {
    return state.criteriaInTemplate
  },
  getCriterion: (state) => (id) => {
    return state.criteria.find(criterion => criterion.id === id)
  },
  getMasterAuditSources (state) {
    return _sortBy(state.sources, source => latinize(source.toLowerCase()))
  },
  getAuditSubSources (state) {
    return _sortBy(state.subSources, subSource => latinize(subSource.name.toLowerCase()))
  },
  getSubsourceById: (state) => (id) => {
    return state.subSources.find(item => item.id === id)
  },
  getMasterAuditTemplates (state) {
    return _sortBy(state.templates, template => latinize(template.name.toLowerCase()))
  },
  getMasterAuditTemplatesBySource: (state) => (source) => {
    return _sortBy(
      state.templates.filter(template => { return template.source == source }),
      template => latinize(template.name.toLowerCase()))
  },
  getTemplateById: (state) => (id) => {
    return state.templates.find(template => template.id === id)
  },
  getMasterAuditPercentageRules (state) {
    return state.percentageRules
  },
  getMasterAuditPercentageRuleById: (state) => (id) => {
    return state.percentageRules.find(rule => rule.id === id)
  },
  getMasterAuditPercentageScores (state) {
    return state.percentageScores
  },
  getStatusAuditSubsourcesBySource: (state) => (source) => {
    return state.statusAuditSubsources.find(item => item.source === source)
  },
}

const mutations = {
  setCriteria (state, value) {
    state.criteria = value
  },
  setCriteriaInTemplate (state, value) {
    state.criteriaInTemplate = value
  },
  setCriterion (state, value) {
    state.criteria.push(value)
  },
  setMasterAuditSources (state, value) {
    state.sources = value
  },
  updateCriterion (state, value) {
    const index = state.criteria.findIndex(criterion => criterion.id === value.id)
    state.criteria.splice(index, 1, value)
  },
  removeCriterion (state, id) {
    state.criteria = state.criteria.filter(criterion => criterion.id !== id)
  },
  setMasterAuditTemplates (state, templates) {
    state.templates = templates
  },
  setAuditConfigByRoles (state, auditConfigByRoles) {
    state.auditConfigByRoles = auditConfigByRoles
  },
  updateMasterAuditTemplate (state, template) {
    const index = state.templates.findIndex(t => t.id === template.id)
    const auxTemplate = { ...state.templates[index], ...template }
    state.templates.splice(index, 1, auxTemplate)
  },
  addMasterAuditTemplate (state, template) {
    const index = state.templates.findIndex(t => t.id === template.id)
    if (index === -1) {
      state.templates.push(template)
    }
  },
  setMasterAuditPercentageRules (state, value) {
    state.percentageRules = value
  },
  setMasterAuditPercentageScores (state, value) {
    state.percentageScores = value
  },
  setMasterAuditSubSources (state, value) {
    state.subSources = value
  },
  setStatusAuditSubsource (state, value) {
    const index = state.statusAuditSubsources.findIndex(item => item.source === value.source)
    if (index > -1) {
      const auxSource = { ...state.statusAuditSubsources[index], ...value }
      state.statusAuditSubsources.splice(index, 1, auxSource)
    } else {
      state.statusAuditSubsources.push(value)
    }
  },
  setStatusAllAuditSubsources (state, value) {
    state.statusAuditSubsources = value
  },
  setTemplateFiltersLoadingStatus (state, status) {
    state.templateFiltersLoadingStatus = status
  },
}

const actions = {
  async deleteCriterion ({ commit }, criterionInTemplate) {
    const criterionID = criterionInTemplate.criterion
    const templateID = criterionInTemplate.template
    await removeCriterionInTemplate(criterionInTemplate.id, templateID)
    commit('removeCriterion', criterionID)
  },
  async updateCriteria ({ commit }, payload) {
    await patchCriteriaInTemplate(payload.selectedTemplateID, payload.data)
    commit('setCriteria', payload.clonedCriteria)
  },
  async updateFamilies (_, payload) {
    await patchTemplateLevelItems(payload.id, payload.data)
  },
  async setLPACriteriaInTemplate ({ commit }, selectedTemplateID) {
    const response = await getLPAsCriteriaInTemplate(selectedTemplateID)
    commit('setCriteriaInTemplate', response)
    return response
  },
  async fetchMasterAuditSources ({ commit }) {
    const sources = await getMasterAuditSources()
    commit('setMasterAuditSources', sources.sources)
    return sources.sources
  },
  async fetchAuditSubSources ({ commit }) {
    const sources = await getAuditSubSources()
    commit('setMasterAuditSubSources', sources)
    return sources
  },
  fetchMasterAuditTemplates ({ commit, state }, force=false) {
    if (state.templateFiltersPromise == null || force) {
      state.templateFiltersPromise = getLPAListTemplates({
        format: 'table',
        active: true,
      })
      commit('setTemplateFiltersLoadingStatus', PROMISE_STATUS.pending)
    }
    const needsUpdate = force || state.templates.length === 0
    return state.templateFiltersPromise.then((templates) => {
      needsUpdate ? commit('setMasterAuditTemplates', templates) : Promise.resolve(templates)
    }).catch(e => {
      state.templateFiltersPromise = null
      throw e
    }).finally(() => {
      commit('setTemplateFiltersLoadingStatus', PROMISE_STATUS.complete)
    })
  },
  fetchMasterAuditTemplatesIfNotFetched ({ state, dispatch }, force=false) {
    if (state.templates.length === 0) {
      return dispatch('fetchMasterAuditTemplates', force)
    }
    return Promise.resolve(state.templates)
  },
  async updateMasterAuditTemplate ({ commit }, payload) {
    const id = payload.id
    delete payload.id
    const template = await patchTemplate(id, payload)
    commit('updateMasterAuditTemplate', template)
    return template
  },
  async fetchAuditConfigByRoles ({ commit }, source) {
    const response = await getAuditConfigByRoles(source)
    const frequencies = _orderBy(response, 'id')
    commit('setAuditConfigByRoles', frequencies)
    return frequencies
  },
  async fetchAuditConfigByTeams (context, source) {
    const response = await getAuditConfigByTeams(source)
    return _orderBy(response, 'id')
  },
  async fetchMasterAuditPercentageRules ({ commit }) {
    const response = await getMasterAuditPercentageRules()
    commit('setMasterAuditPercentageRules', response)
    return response
  },
  async fetchMasterAuditPercentageScores ({ commit }) {
    const response = await getMasterAuditPercentageScores()
    commit('setMasterAuditPercentageScores', response)
    return response
  },
  async updateAuditSubsources ({ commit }, data) {
    const response = await updateAuditTemplateSubtypes(data, data.source)
    const source = {
      value: response.value,
      source: data.source,
    }
    commit('setStatusAuditSubsource', source)
    return response
  },
  async fetchStatusAuditSubsources ({ commit }) {
    const response = await getAuditTemplateSubsources()
    const data = []
    const dataObject = response[0].value
    for (const source in dataObject) {
      data.push({
        source: source,
        value: dataObject[source],
      })
    }
    commit('setStatusAllAuditSubsources', data)
  },
}

export default {
  state: stateObj,
  getters: moduleGetters,
  mutations,
  actions,
}
